import { useMemo } from 'react';
import Table, { Header } from 'src/components/Table';
import { Button } from 'src/components/Button';
import { Heading } from 'src/components/Heading';
import { WarningPanel } from 'src/components/WarningPanel';
import { RadioGroup } from 'src/components/RadioGroup';
import { expandExpressions } from 'src/utils/expressionUtils';
import { EVENT_EXPRESSION_ROOT } from 'src/pages/Automations/components/wizard/WizardContext';
import { ContactFilter } from '../../ContactFilter';
import { useCrmSync } from '../../contexts/CrmSyncContext';
import { useFieldMapping } from '../../contexts/FieldMappingContext';
import {
  useContacts,
  DuplicateHandlingOption,
} from '../../contexts/ContactsContext';
import { useCampaignContext } from '../../contexts/CampaignContext';
import { Can, Subject } from 'src/hooks/usePermissions';

export const PreviewImport = () => {
  const { syncInProgress, handleStepNavigation, navigateToReturnTo } =
    useCrmSync();
  const { fieldMapping } = useFieldMapping();
  const {
    handleImport,
    previewContacts,
    isLoading,
    isImporting,
    duplicateHandlingOption,
    setDuplicateHandlingOption,
  } = useContacts();
  const { campaign } = useCampaignContext();

  const disabled = syncInProgress || isLoading || isImporting;

  const onImport = async () => {
    const syncSummary = await handleImport();

    if (campaign) {
      navigateToReturnTo({ state: { syncSummary } });
    }
  };

  const onBack = () => handleStepNavigation('back');

  const previewContactsSlice = previewContacts.slice(0, 10);

  const campaignFieldKeys = useMemo(
    () => Object.keys(fieldMapping),
    [fieldMapping],
  );

  const headers: Header[] = useMemo(
    () =>
      campaignFieldKeys.map((campaignField) => ({
        key: campaignField,
        label: campaignField.replace(/^context\./, ''),
        width: '150px',
        disableSorting: true,
      })),
    [campaignFieldKeys],
  );

  const rows = useMemo(() => {
    const formatValue = (value: unknown): string => {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch {
          return String(value);
        }
      }
      return String(value);
    };

    return previewContactsSlice.map((contactRecord) => {
      let mappedParams: Record<string, unknown> = {};
      try {
        mappedParams = expandExpressions(
          fieldMapping as Record<string, unknown>,
          {
            [EVENT_EXPRESSION_ROOT]: contactRecord,
          },
        );
      } catch {
        mappedParams = {};
      }

      const row: Record<string, string> = {};
      campaignFieldKeys.forEach((campaignField) => {
        row[campaignField] = formatValue(mappedParams[campaignField]);
      });
      return row;
    });
  }, [campaignFieldKeys, fieldMapping, previewContactsSlice]);

  return (
    <>
      <Heading
        title="Preview Import"
        subtitle="Preview the data you're about to import."
        backAction={onBack}
      >
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outlined"
            onClick={onBack}
            disabled={disabled}
          >
            Cancel
          </Button>
          <Can operation="update" subject={Subject.CAMPAIGNS}>
            <Button
              type="button"
              variant="contained"
              onClick={onImport}
              disabled={disabled}
            >
              Import
            </Button>
          </Can>
        </div>
      </Heading>

      <div className="w-full p-6">
        <div className="space-y-6">
          {/* No Contacts Warning */}
          <WarningPanel
            show={previewContacts.length === 0}
            title="No Contacts Available"
          >
            No contacts were found in your CRM or all contacts have been
            filtered out. Please check your CRM data or adjust your filters.
          </WarningPanel>

          {/* Filter Section */}
          <div className="mb-8">
            <ContactFilter disabled={disabled} />
          </div>

          {/* Contact Summary */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-neutral-700">
              Previewing {previewContacts.length} Contact
              {previewContacts.length === 1 ? '' : 's'}
            </h3>
            <span className="text-sm text-neutral-500">
              Previewing first {Math.min(10, previewContacts.length)}
            </span>
          </div>

          {/* Preview Table */}
          <Table
            headers={headers}
            rows={rows}
            totalItems={rows.length}
            currentPage={1}
            onPageChange={() => {}}
            onSort={() => {}}
            loading={false}
            setSize={() => {}}
            defaultSize={10}
            onRowClick={() => {}}
          />

          {/* Duplicate Handling */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-700 mb-2">
              If a contact with the same phone number already exists, what
              should we do?
            </h3>
            <RadioGroup
              items={[
                {
                  value: DuplicateHandlingOption.SKIP,
                  content: (
                    <div>
                      <div className="text-neutral-700">Skip</div>
                      <div className="text-neutral-500 text-sm">
                        Don’t add this contact. The existing one stays as is.
                      </div>
                    </div>
                  ),
                },
                {
                  value: DuplicateHandlingOption.KEEP_BOTH,
                  content: (
                    <div>
                      <div className="text-neutral-700">Keep both</div>
                      <div className="text-neutral-500 text-sm">
                        Add this as a new contact and keep the existing one.
                      </div>
                    </div>
                  ),
                },
                {
                  value: DuplicateHandlingOption.UPDATE,
                  content: (
                    <div>
                      <div className="text-neutral-700">Update</div>
                      <div className="text-neutral-500 text-sm">
                        Replace the existing contact&apos;s details with this
                        one.
                      </div>
                    </div>
                  ),
                },
              ]}
              defaultValue={duplicateHandlingOption}
              onChange={(value) =>
                setDuplicateHandlingOption(value as DuplicateHandlingOption)
              }
              className="mt-2"
            />
          </div>
        </div>
      </div>
    </>
  );
};
